/**
 * Client-side parsers for SEC EDGAR data.
 *
 * Precondition: Data from SEC EDGAR API
 * Postcondition: Normalized, typed data structures
 */

export interface Submission {
  filingDate: string;
  reportDate?: string;
  form?: string;
  accessionNumber?: string;
  restricted?: boolean;
}

export interface CompanyData {
  cik: string;
  name: string;
  entityType?: string;
  sic?: string;
  sicDescription?: string;
  tickers?: string[];
  exchanges?: string[];
  ein?: string;
  category?: string;
  fiscalYearEnd?: string;
  stateOfIncorporation?: string;
  stateOfIncorporationDescription?: string;
  website?: string;
  investorWebsite?: string;
  phone?: string;
  insiderTransactionForOwnerExists?: boolean;
  insiderTransactionForIssuerExists?: boolean;
  addresses?: {
    business?: Address;
    mailing?: Address;
  };
  formerNames?: { name: string; to: string }[];
}

interface Address {
  street1?: string;
  street2?: string;
  city?: string;
  stateOrCountry?: string;
  zipCode?: string;
}

export function getQuarter(filingDate: string): string {
  const date = new Date(filingDate);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const quarter = Math.ceil(month / 3);
  return `${year}-Q${quarter}`;
}

export function getPreviousQuarter(quarter: string): string {
  const match = quarter.match(/^(\d{4})-Q([1-4])$/);
  if (!match) return quarter;

  const year = parseInt(match[1], 10);
  const q = parseInt(match[2], 10);

  if (q === 1) {
    return `${year - 1}-Q4`;
  }
  return `${year}-Q${q - 1}`;
}

// Converts "2024-Q1" to "Q1 2024" for display
export function formatQuarterLabel(quarter: string | null | undefined): string {
  if (!quarter) return '';
  const match = quarter.match(/^(\d{4})-Q([1-4])$/);
  if (!match) return quarter;
  return `Q${match[2]} ${match[1]}`;
}

/**
 * Get the latest quarter from parsed submissions.
 * Uses reportDate (quarter end) only - filingDate is when submitted, not the quarter.
 * Postcondition: Returns quarter string or null if no submissions with reportDate
 */
export function getLatestQuarter(submissions: Submission[]): string | null {
  if (submissions.length === 0) return null;

  let latestDate = '';
  for (const sub of submissions) {
    if (sub.reportDate && sub.reportDate > latestDate) {
      latestDate = sub.reportDate;
    }
  }
  return latestDate ? getQuarter(latestDate) : null;
}

export interface QuarterInfo {
  quarter: string;
  restricted: boolean;
}

export function groupByYear(submissions: Submission[]): Record<string, Map<string, QuarterInfo>> {
  const yearMap: Record<string, Map<string, QuarterInfo>> = {};
  submissions.forEach((sub) => {
    if (!sub.reportDate) return;
    const quarter = getQuarter(sub.reportDate);
    const [year, q] = quarter.split('-');
    if (!yearMap[year]) yearMap[year] = new Map();
    // Only set if not already present (first filing for quarter wins)
    if (!yearMap[year].has(q)) {
      yearMap[year].set(q, { quarter: q, restricted: sub.restricted ?? false });
    }
  });
  return yearMap;
}

const REDACTED_ACCESSION = "0000000000-00-000000";

export function parseEdgarSubmissions(data: any): Submission[] {
  const recent = data.filings?.recent;
  if (!recent || !recent.filingDate || !recent.filingDate.length) {
    return [];
  }

  return recent.filingDate
    .map((date: string, i: number) => ({
      filingDate: date,
      reportDate: recent.reportDate?.[i],
      form: recent.form?.[i] || recent.core_type?.[i],
      accessionNumber: recent.accessionNumber?.[i],
      restricted: recent.accessionNumber?.[i] === REDACTED_ACCESSION,
    }))
    .filter((sub: Submission) => sub.form === '13F-HR' || sub.form === '13F-HR/A');
}

export function parseCompanyData(data: any): CompanyData {
  return {
    cik: data.cik || '',
    name: data.name || '',
    entityType: data.entityType,
    sic: data.sic,
    sicDescription: data.sicDescription,
    tickers: data.tickers,
    exchanges: data.exchanges,
    ein: data.ein,
    category: data.category,
    fiscalYearEnd: data.fiscalYearEnd,
    stateOfIncorporation: data.stateOfIncorporation,
    stateOfIncorporationDescription: data.stateOfIncorporationDescription,
    website: data.website,
    investorWebsite: data.investorWebsite,
    phone: data.phone,
    insiderTransactionForOwnerExists: data.insiderTransactionForOwnerExists,
    insiderTransactionForIssuerExists: data.insiderTransactionForIssuerExists,
    addresses: data.addresses,
    formerNames: data.formerNames,
  };
}
